import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useSchoolSettings } from '@/context/SchoolSettingsContext';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Users, GraduationCap, Trophy, Settings, TrendingUp, Award, Archive, UserCheck, Building2 } from 'lucide-react';
import MessageBell from '@/components/MessageBell';
import ProfileSettings from '@/components/ProfileSettings';
import SchoolSettingsPanel from '@/components/SchoolSettingsPanel';
import { Button } from '@/components/ui/button';
import AdminHome from '@/pages/admin/AdminHome';
import ClassDetail from '@/pages/admin/ClassDetail';
import StudentDetail from '@/pages/admin/StudentDetail';
import BehaviorManagement from '@/pages/admin/BehaviorManagement';
import UserManagement from '@/pages/admin/UserManagement';
import ArchiveManagement from '@/pages/admin/ArchiveManagement';
import TeacherStats from '@/pages/admin/TeacherStats';
import SystemSettings from '@/pages/admin/Settings';
import Reports from '@/pages/admin/Reports';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Merit Plus Light Theme Colors
const colors = {
  canvas: '#F8FAFC',
  surface: '#FFFFFF',
  card: '#FFFFFF',
  cardHover: '#F1F5F9',
  teal: '#0D9488',
  tealHover: '#0F766E',
  indigo: '#4F46E5',
  merit: '#10B981',
  demerit: '#EF4444',
  textPrimary: '#0F172A',
  textSecondary: '#475569',
  textMuted: '#64748B',
  border: '#E2E8F0',
};

const LOGO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_a05533bd-d761-4a3f-beb7-e7e62bdd4f88/artifacts/glvqb9sk_ChatGPT%20Image%20Aug%2013%2C%202026%20at%2002_26_31%20PM.png";

export default function AdminDashboard() {
  const { user, logout, getAuthHeaders } = useAuth();
  const { settings: schoolSettings } = useSchoolSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const { subdomain } = useParams();
  
  // Base path for admin routes
  const basePath = `/${subdomain}/admin`;

  const handleLogout = () => {
    logout();
    localStorage.removeItem('school_subdomain');
    navigate(subdomain ? `/${subdomain}` : '/');
    toast.success('Сәтті шықтыңыз');
  };

  const navItems = [
    { path: basePath, label: 'Басты бет', shortLabel: 'Басты', icon: Trophy, testId: 'admin-home-nav' },
    { path: `${basePath}/behaviors`, label: 'Мінез-құлықтар', shortLabel: 'Мінез', icon: Award, testId: 'behaviors-nav' },
    { path: `${basePath}/users`, label: 'Пайдаланушылар', shortLabel: 'Қолд.', icon: Users, testId: 'users-nav' },
    { path: `${basePath}/teachers`, label: 'Мұғалімдер', shortLabel: 'Мұғ.', icon: UserCheck, testId: 'teachers-nav' },
    { path: `${basePath}/reports`, label: 'Есептер', shortLabel: 'Есеп', icon: TrendingUp, testId: 'reports-nav' },
    { path: `${basePath}/archive`, label: 'Мұрағат', shortLabel: 'Мұрағат', icon: Archive, testId: 'archive-nav' },
    { path: `${basePath}/school`, label: 'Мектеп', shortLabel: 'Мект.', icon: Building2, testId: 'school-nav' },
    { path: `${basePath}/settings`, label: 'Баптаулар', shortLabel: 'Бапт.', icon: Settings, testId: 'settings-nav' },
  ];

  const isActiveRoute = (path) => {
    if (path === basePath) {
      return location.pathname === basePath || location.pathname === `${basePath}/`;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-18">
            <div className="flex items-center gap-3 sm:gap-4">
              <img 
                src={schoolSettings?.logo_url || LOGO_URL}
                alt={schoolSettings?.school_name || 'Merit Plus'}
                className="w-10 h-10 sm:w-11 sm:h-11 object-contain rounded-lg shadow-sm"
              />
              <div>
                <h1 className="text-lg sm:text-xl font-bold tracking-tight text-slate-900">
                  {schoolSettings?.school_name || 'Merit Plus'}
                </h1>
                <p className="text-xs sm:text-sm font-medium text-teal-600">
                  Admin панелі
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              <div className="hidden md:block text-right">
                <p className="text-sm font-semibold text-slate-900">{user?.full_name}</p>
                <p className="text-xs text-slate-500">Admin</p>
              </div>
              <MessageBell className="border border-slate-200 bg-white hover:bg-slate-50 text-slate-700" />
              <ProfileSettings user={user} />
              <Button 
                variant="outline" 
                size="icon" 
                onClick={handleLogout}
                className="text-slate-500 hover:text-red-600 hover:bg-red-50 border-slate-200"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="sticky top-16 sm:top-[4.5rem] z-40 bg-white border-b border-slate-200 overflow-x-auto shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1 min-w-max py-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActiveRoute(item.path);
              return (
                <Link key={item.path} to={item.path} data-testid={item.testId}>
                  <button 
                    className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all whitespace-nowrap flex items-center gap-1.5 ${
                      isActive 
                        ? 'bg-teal-50 text-teal-700 border border-teal-200' 
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-transparent'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">{item.label}</span>
                    <span className="sm:hidden">{item.shortLabel}</span>
                  </button>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Routes>
          <Route index element={<AdminHome />} />
          <Route path="class/:classId" element={<ClassDetail />} />
          <Route path="student/:studentId" element={<StudentDetail />} />
          <Route path="behaviors" element={<BehaviorManagement />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="teachers" element={<TeacherStats />} />
          <Route path="reports" element={<Reports />} />
          <Route path="archive" element={<ArchiveManagement />} />
          <Route path="school" element={<SchoolSettingsPanel />} />
          <Route path="settings" element={<SystemSettings />} />
        </Routes>
      </main>
    </div>
  );
}
